import React, { useCallback, useEffect, useRef, useState } from "react"
import { Modal, Button, Flex } from "antd"
import ReactCrop, { centerCrop, makeAspectCrop, convertToPixelCrop } from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import styles from "./index.module.css"

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  // Choose the largest crop that fits the image while maintaining aspect.
  // If the image is wider than target aspect, height will hit 100% and width < 100%.
  // If the image is narrower than target aspect, width will hit 100% and height < 100%.
  const imageAspect = mediaWidth / mediaHeight
  const widthPercent = Math.min(100, (aspect / imageAspect) * 100)
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: widthPercent
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  )
}

const ImageCropper = ({ visible, onClose, imageSrc, onCropComplete, targetWidth = 1080, targetHeight = 1080, aspect, lockSize = true, minNaturalWidth, minNaturalHeight }) => {
  const [imageLoading, setImageLoading] = useState(true)
  const { t } = useTranslation()
  const [crop, setCrop] = useState()
  const [completedCrop, setCompletedCrop] = useState(null)
  const [imageRef, setImageRef] = useState(null)
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 })
  const [cropDimensions, setCropDimensions] = useState({ width: 0, height: 0 })
  const [minDisplayDimensions, setMinDisplayDimensions] = useState({ width: null, height: null })
  const [isCropping, setIsCropping] = useState(false)
  const imgRef = useRef(null)
  const baseCropRef = useRef(null)

  // Reset crop state when image source changes or modal is opened/closed
  useEffect(() => {
    if (visible && imageSrc) {
      // Only reset if we have a new image or the modal was closed and reopened
      setCrop(undefined)
      setCompletedCrop(null)
      setImageDimensions({ width: 0, height: 0 })
    }
  }, [visible, imageSrc])

  const onLoad = useCallback(
    (img) => {
      if (!img) return

      imgRef.current = img
      setImageRef(img)
      setImageLoading(false)

      const { naturalWidth: width, naturalHeight: height } = img
      setImageDimensions({ width, height })

      // Compute minimum display-pixel dimensions from natural-pixel minimums
      if (minNaturalWidth || minNaturalHeight) {
        const displayW = img.width
        const displayH = img.height
        setMinDisplayDimensions({
          width: minNaturalWidth ? Math.floor(minNaturalWidth * (displayW / width)) : null,
          height: minNaturalHeight ? Math.floor(minNaturalHeight * (displayH / height)) : null
        })
      }

      // Calculate initial crop (supports both fixed-aspect and freeform)
      if (aspect) {
        const newCrop = centerAspectCrop(width, height, aspect)
        baseCropRef.current = newCrop
        setCrop(newCrop)
        setCompletedCrop(convertToPixelCrop(newCrop, img.width, img.height))

        // Set initial crop dimensions display - manually calculate to ensure accuracy
        const cropWidthPx = Math.round((newCrop.width / 100) * width)
        const cropHeightPx = Math.round((newCrop.height / 100) * height)
        setCropDimensions({ width: cropWidthPx, height: cropHeightPx })
      } else {
        // Freeform: create a large centered crop without locking aspect
        const percentWidth = 90
        const percentHeight = Math.min(90, (percentWidth * height) / width) // keep within bounds
        const newCrop = centerCrop(
          {
            unit: "%",
            width: percentWidth,
            height: percentHeight
          },
          width,
          height
        )
        baseCropRef.current = newCrop
        setCrop(newCrop)
        setCompletedCrop(convertToPixelCrop(newCrop, img.width, img.height))

        // Set initial crop dimensions display - manually calculate to ensure accuracy
        const cropWidthPx = Math.round((newCrop.width / 100) * width)
        const cropHeightPx = Math.round((newCrop.height / 100) * height)
        setCropDimensions({ width: cropWidthPx, height: cropHeightPx })
      }
    },
    [aspect, minNaturalWidth, minNaturalHeight]
  )

  const handleCropComplete = (crop) => {
    setCompletedCrop(crop)

    // When lockSize is enabled, always use baseCropRef for dimension calculation
    // to avoid rounding errors from pixel unit conversions
    if (lockSize && baseCropRef.current && imageRef) {
      const base = baseCropRef.current
      const cropWidthPx = Math.round((base.width / 100) * imageRef.naturalWidth)
      const cropHeightPx = Math.round((base.height / 100) * imageRef.naturalHeight)
      setCropDimensions({ width: cropWidthPx, height: cropHeightPx })
      return
    }

    // Update cropDimensions on crop complete (normal resizable mode)
    if (imageRef && crop?.width && crop?.height) {
      let cropWidthPx, cropHeightPx

      if (crop.unit === '%') {
        // Crop is in percentage units
        cropWidthPx = Math.round((crop.width / 100) * imageRef.naturalWidth)
        cropHeightPx = Math.round((crop.height / 100) * imageRef.naturalHeight)
      } else {
        // Crop is in pixel units of the displayed image, scale to natural size
        const scaleX = imageRef.naturalWidth / imageRef.width
        const scaleY = imageRef.naturalHeight / imageRef.height
        cropWidthPx = Math.round(crop.width * scaleX)
        cropHeightPx = Math.round(crop.height * scaleY)
      }

      setCropDimensions({ width: cropWidthPx, height: cropHeightPx })
    }
  }

  const handleCrop = () => {
    if (!imageRef || !completedCrop) return
    setIsCropping(true)

    let cropX, cropY, cropWidth, cropHeight

    // When lockSize is enabled, use baseCropRef to avoid rounding errors
    if (lockSize && baseCropRef.current && crop) {
      const base = baseCropRef.current
      // Use current crop position (x, y) but base dimensions (width, height)
      const cropXPercent = crop.x ?? 0
      const cropYPercent = crop.y ?? 0

      cropX = Math.round((cropXPercent / 100) * imageRef.naturalWidth)
      cropY = Math.round((cropYPercent / 100) * imageRef.naturalHeight)
      cropWidth = Math.round((base.width / 100) * imageRef.naturalWidth)
      cropHeight = Math.round((base.height / 100) * imageRef.naturalHeight)
    } else {
      // Calculate crop dimensions in original image coordinates
      const scaleX = imageRef.naturalWidth / imageRef.width
      const scaleY = imageRef.naturalHeight / imageRef.height
      cropX = Math.round(completedCrop.x * scaleX)
      cropY = Math.round(completedCrop.y * scaleY)
      cropWidth = Math.round(completedCrop.width * scaleX)
      cropHeight = Math.round(completedCrop.height * scaleY)
    }

    // Prepare crop info for backend
    const cropInfo = {
      x: cropX,
      y: cropY,
      width: cropWidth,
      height: cropHeight,
      originalWidth: imageRef.naturalWidth,
      originalHeight: imageRef.naturalHeight
    }

    // Optionally, create a preview blob (for UI, not backend)
    const canvas = document.createElement("canvas")
    canvas.width = cropWidth
    canvas.height = cropHeight
    const ctx = canvas.getContext("2d")
    ctx.drawImage(imageRef, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight)
    canvas.toBlob(
      (blob) => {
        if (blob) {
          // Send both blob (for preview) and cropInfo (for backend)
          onCropComplete(blob, cropInfo)
        }
        setIsCropping(false)
      },
      "image/jpeg",
      0.9
    )
  }

  if (!imageSrc) return null

  // Update cropDimensions live as user changes crop
  const handleCropChange = (c) => {
    // If locking size, keep width/height fixed to initial crop, only update x/y (clamped)
    if (lockSize && baseCropRef.current) {
      const base = baseCropRef.current

      // Convert incoming crop to percentage if needed
      let incomingX = c.x ?? 0
      let incomingY = c.y ?? 0

      if (c.unit === 'px' && imageRef) {
        // Convert from displayed pixels to percentage
        incomingX = (c.x / imageRef.width) * 100
        incomingY = (c.y / imageRef.height) * 100
      }

      const maxX = Math.max(0, 100 - (base.width || 0))
      const maxY = Math.max(0, 100 - (base.height || 0))
      const next = {
        unit: "%",  // Always use percentage
        x: Math.max(0, Math.min(incomingX, maxX)),
        y: Math.max(0, Math.min(incomingY, maxY)),
        width: base.width,
        height: base.height
      }

      setCrop(next)
      if (imageRef && next.width && next.height) {
        let cropWidthPx, cropHeightPx

        if (next.unit === '%') {
          cropWidthPx = Math.round((next.width / 100) * imageRef.naturalWidth)
          cropHeightPx = Math.round((next.height / 100) * imageRef.naturalHeight)
        } else {
          const scaleX = imageRef.naturalWidth / imageRef.width
          const scaleY = imageRef.naturalHeight / imageRef.height
          cropWidthPx = Math.round(next.width * scaleX)
          cropHeightPx = Math.round(next.height * scaleY)
        }

        setCropDimensions({ width: cropWidthPx, height: cropHeightPx })
      }
      return
    }

    // Default behavior (resizable)
    if (imageRef && c.width && c.height) {
      let cropWidthPx, cropHeightPx

      if (c.unit === '%') {
        cropWidthPx = Math.round((c.width / 100) * imageRef.naturalWidth)
        cropHeightPx = Math.round((c.height / 100) * imageRef.naturalHeight)
      } else {
        const scaleX = imageRef.naturalWidth / imageRef.width
        const scaleY = imageRef.naturalHeight / imageRef.height
        cropWidthPx = Math.round(c.width * scaleX)
        cropHeightPx = Math.round(c.height * scaleY)
      }

      // Snap to exact natural pixel minimum using % units — avoids display-to-natural rounding errors
      const belowMinW = minNaturalWidth && cropWidthPx < minNaturalWidth
      const belowMinH = minNaturalHeight && cropHeightPx < minNaturalHeight
      if (belowMinW || belowMinH) {
        const xPct = c.unit === '%' ? c.x : (c.x / imageRef.width) * 100
        const yPct = c.unit === '%' ? c.y : (c.y / imageRef.height) * 100
        const currentWidthPct = c.unit === '%' ? c.width : (c.width / imageRef.width) * 100
        const currentHeightPct = c.unit === '%' ? c.height : (c.height / imageRef.height) * 100
        const snapWidthPct = belowMinW ? (minNaturalWidth / imageRef.naturalWidth) * 100 : currentWidthPct
        const snapHeightPct = belowMinH ? (minNaturalHeight / imageRef.naturalHeight) * 100 : currentHeightPct
        setCrop({
          unit: '%',
          x: Math.max(0, Math.min(xPct, 100 - snapWidthPct)),
          y: Math.max(0, Math.min(yPct, 100 - snapHeightPct)),
          width: snapWidthPct,
          height: snapHeightPct
        })
        setCropDimensions({
          width: belowMinW ? minNaturalWidth : cropWidthPx,
          height: belowMinH ? minNaturalHeight : cropHeightPx
        })
        return
      }

      setCrop(c)
      setCropDimensions({ width: cropWidthPx, height: cropHeightPx })
      return
    }

    setCrop(c)
  }

  return (
    <Modal
      title={t("listings.tabs.media.edit_image_crop")}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      className={styles.imageCropperModal}
      maskClosable={false}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.helperText}>{t("listings.tabs.media.drag_to_resize_and_reposition")}</div>
          <Flex justify="center" align="center" className={styles.canvasWrap}>
            <Flex vertical className={styles.cropCard}>
              {/* Loader overlay while image is loading */}
              {imageLoading && (
                <div className={styles.loaderOverlay}>
                  <span className="ant-spin ant-spin-spinning" style={{ fontSize: 32 }} />
                </div>
              )}
              <ReactCrop
                crop={crop}
                onChange={handleCropChange}
                onComplete={handleCropComplete}
                aspect={aspect}
                className={styles.reactCrop}
                keepSelection={true}
                minWidth={minDisplayDimensions.width ?? 100}
                minHeight={minDisplayDimensions.height ?? (typeof aspect === "number" && !Number.isNaN(aspect) && aspect > 0 ? Math.round(100 * (1 / aspect)) : 50)}
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  crossOrigin="anonymous"
                  onLoad={(e) => {
                    onLoad(e.currentTarget)
                  }}
                  alt="Crop preview"
                  className={styles.image}
                />
              </ReactCrop>
              {/* Resolution display */}
              <div className={styles.resolution}>
                {imageDimensions.width} × {imageDimensions.height}
                {cropDimensions.width > 0 && cropDimensions.height > 0 && (
                  <>
                    {" "}
                    → {cropDimensions.width} × {cropDimensions.height}
                  </>
                )}
              </div>
            </Flex>
          </Flex>
        </div>
        {/* Footer Actions */}
        <div className={styles.footer}>
          <Button key="cancel" onClick={onClose}>
            {t("global.cancel")}
          </Button>
          <Button key="crop" type="primary" onClick={handleCrop} loading={isCropping} disabled={!completedCrop}>
            {t("global.update")}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

ImageCropper.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  imageSrc: PropTypes.string.isRequired,
  onCropComplete: PropTypes.func.isRequired,
  targetWidth: PropTypes.number,
  targetHeight: PropTypes.number,
  aspect: PropTypes.number,
  lockSize: PropTypes.bool,
  minNaturalWidth: PropTypes.number,
  minNaturalHeight: PropTypes.number
}

export default ImageCropper
